/**
 * Landing-page sections that are mostly content-driven: trust strip,
 * protection layers, process timeline, use cases, "what you can prove",
 * and the final CTA. Each is mobile-first (single column, wrapping) and
 * expands to multi-column layouts from md/lg.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./icons";
import {
  PROCESS_STEPS,
  PROOF_CARDS,
  PROTECTION_LAYERS,
  TRUST_ITEMS,
  USE_CASES,
} from "./content";

/* ------------------------------------------------------------------ */
/* Shared primitives                                                    */
/* ------------------------------------------------------------------ */

export function Section({
  id,
  eyebrow,
  title,
  intro,
  children,
  tone = "white",
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  intro?: string;
  children: ReactNode;
  tone?: "white" | "soft";
}) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={`scroll-mt-20 py-14 sm:py-20 lg:py-24 ${tone === "soft" ? "bg-bg-soft" : "bg-white"}`}
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h2 id={headingId} className="text-[1.75rem] leading-[1.15] sm:text-[2.125rem] lg:text-[2.5rem]">
            {title}
          </h2>
          {intro && <p className="mt-4 text-base leading-relaxed text-ink-soft sm:text-lg">{intro}</p>}
        </div>
        <div className="mt-10 sm:mt-12">{children}</div>
      </div>
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-aigentico-red">{children}</p>
  );
}

function IconDisc({ name, tone = "navy" }: { name: Parameters<typeof Icon>[0]["name"]; tone?: "navy" | "red" }) {
  const cls =
    tone === "navy"
      ? "bg-aigentico-navy/[0.06] text-aigentico-navy"
      : "bg-aigentico-red/[0.07] text-aigentico-red";
  return (
    <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${cls}`}>
      <Icon name={name} size={22} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Trust strip                                                          */
/* ------------------------------------------------------------------ */

export function TrustStrip() {
  return (
    <section aria-label="What protects your evidence" className="border-y border-border bg-bg-soft">
      <ul className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-x-6 gap-y-3 px-4 py-5 sm:grid-cols-2 sm:px-6 lg:grid-cols-5 lg:gap-x-4 lg:py-6">
        {TRUST_ITEMS.map((t) => (
          <li key={t.label} className="flex items-center gap-3 text-sm font-medium text-ink lg:flex-col lg:items-start lg:gap-2">
            <Icon name={t.icon} size={22} className="shrink-0 text-aigentico-navy" />
            <span className="leading-snug">{t.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Features — three layers                                              */
/* ------------------------------------------------------------------ */

export function ProtectionLayers() {
  return (
    <Section
      id="features"
      eyebrow="Features"
      title="Three independent layers of protection"
      intro="Each finalised evidence record is protected in three ways at once. Every layer can be checked on its own, and together they make later disputes about integrity far easier to settle."
    >
      <ol className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        {PROTECTION_LAYERS.map((layer, i) => (
          <li key={layer.eyebrow} className="flex flex-col rounded-xl border border-border bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <IconDisc name={layer.icon} />
              <span className="font-mono text-xs text-ink-faint">0{i + 1}</span>
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-aigentico-red">{layer.eyebrow}</p>
            <h3 className="mt-1 font-sans text-lg font-semibold tracking-normal text-ink">{layer.title}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{layer.body}</p>
            <p className="mt-4 flex items-start gap-2 border-t border-border pt-4 text-sm font-medium text-aigentico-navy">
              <Icon name="check" size={18} className="mt-0.5 shrink-0" />
              <span>{layer.outcome}</span>
            </p>
          </li>
        ))}
      </ol>

      <div className="mt-6 flex flex-col gap-4 rounded-xl border border-border bg-bg-soft p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="flex items-start gap-3 text-sm text-ink-soft">
          <Icon name="noCloud" size={20} className="mt-0.5 shrink-0 text-aigentico-navy" />
          <span>
            Only a cryptographic proof is anchored — not your files, media, or personal data.
          </span>
        </p>
        <p className="shrink-0 text-sm font-semibold text-aigentico-navy">
          One finalised record. Three independent checks.
        </p>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* How it works — five steps                                            */
/* ------------------------------------------------------------------ */

export function ProcessTimeline() {
  return (
    <Section
      id="how-it-works"
      eyebrow="How it works"
      title="Capture once. Prove later."
      intro="Five steps from a file on your phone to a finalised evidence record you can check years from now."
      tone="soft"
    >
      <ol className="relative grid grid-cols-1 gap-0 lg:grid-cols-5 lg:gap-4">
        {PROCESS_STEPS.map((step, i) => {
          const last = i === PROCESS_STEPS.length - 1;
          return (
            <li key={step.title} className="relative flex gap-4 lg:block">
              {/* Mobile vertical spine */}
              <div className="flex flex-col items-center lg:hidden" aria-hidden="true">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-aigentico-navy text-white">
                  <Icon name={step.icon} size={20} />
                </span>
                {!last && <span className="my-1 w-px flex-1 bg-border-strong" />}
              </div>

              {/* Desktop horizontal spine */}
              <div className="hidden items-center lg:flex" aria-hidden="true">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-aigentico-navy text-white">
                  <Icon name={step.icon} size={22} />
                </span>
                {!last && <span className="ml-3 h-px flex-1 bg-border-strong" />}
              </div>

              <div className={`min-w-0 flex-1 ${last ? "pb-0" : "pb-8"} lg:pb-0 lg:pt-4`}>
                <p className="font-mono text-xs text-ink-faint">Step {i + 1}</p>
                <h3 className="mt-1 font-sans text-base font-semibold tracking-normal text-ink sm:text-lg">
                  {step.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{step.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Use cases                                                            */
/* ------------------------------------------------------------------ */

export function UseCases() {
  return (
    <Section
      id="use-cases"
      eyebrow="Use cases"
      title="Useful anywhere proof matters"
      intro="Whenever someone may later ask what a file looked like, when it existed, or whether it has changed."
    >
      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {USE_CASES.map((u) => (
          <li key={u.title} className="flex gap-4 rounded-xl border border-border bg-white p-5">
            <IconDisc name={u.icon} />
            <div className="min-w-0">
              <h3 className="font-sans text-base font-semibold tracking-normal text-ink">{u.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{u.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-14 sm:mt-16">
        <h3 className="font-sans text-xl font-semibold tracking-normal text-ink sm:text-2xl">
          What you can prove with StampmatS
        </h3>
        <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {PROOF_CARDS.map((c) => (
            <li key={c.title} className="rounded-xl border-l-4 border-aigentico-navy bg-bg-soft p-5">
              <div className="flex items-center gap-3">
                <Icon name={c.icon} size={22} className="shrink-0 text-aigentico-navy" />
                <h4 className="font-sans text-base font-semibold tracking-normal text-ink">{c.title}</h4>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">{c.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Final CTA                                                            */
/* ------------------------------------------------------------------ */

export function FinalCta() {
  return (
    <section aria-labelledby="cta-title" className="bg-white py-14 sm:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="rounded-2xl border-2 border-aigentico-navy px-5 py-10 text-center sm:px-10 sm:py-14">
          <h2 id="cta-title" className="text-[1.75rem] leading-[1.15] sm:text-[2.125rem] lg:text-[2.5rem]">
            Protect digital evidence with stronger proof
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg">
            Create an account and finalise your first file. Your evidence gets sealed, chained, and
            independently time-proven.
          </p>
          <div className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row sm:justify-center">
            <Link to="/signup" className="btn-primary min-h-[48px] w-full sm:w-auto">
              Create account
            </Link>
            <Link to="/signin" className="btn-secondary min-h-[48px] w-full sm:w-auto">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
